/**
 * Extraction zone hold logic: team must maintain presence at a location.
 * Pure functions — no side effects, no rendering.
 */

import type { PlayerState, PlanetState, ExtractionProgress, Vec3 } from '../types';
import { EXTRACTION_RANGE } from '../constants';
import { arcDistance } from './gameState';

/**
 * Check if a player is within extraction zone range.
 */
export function isInExtractionZone(
  player: PlayerState,
  target: { planetIndex: number; surfaceDir: Vec3 },
  planets: PlanetState[]
): boolean {
  if (!player.alive || player.airborne) return false;
  if (player.planetIndex !== target.planetIndex) return false;

  const planet = planets[target.planetIndex];
  if (!planet) return false;

  const dist = arcDistance(player.surfaceDir, target.surfaceDir, planet.radius);
  return dist <= EXTRACTION_RANGE;
}

/**
 * Get all alive team units currently in the extraction zone.
 */
export function getTeamUnitsInZone(
  players: PlayerState[],
  teamIndex: number,
  target: { planetIndex: number; surfaceDir: Vec3 },
  planets: PlanetState[]
): PlayerState[] {
  return players.filter(
    p => p.teamIndex === teamIndex && isInExtractionZone(p, target, planets)
  );
}

/**
 * Create a new extraction progress tracker.
 */
export function startExtraction(
  teamIndex: number,
  target: { planetIndex: number; surfaceDir: Vec3 },
  turnsRequired: number
): ExtractionProgress {
  return {
    teamIndex,
    planetIndex: target.planetIndex,
    surfaceDir: { ...target.surfaceDir },
    turnsHeld: 0,
    turnsRequired,
  };
}

/**
 * Tick extraction progress.
 * If any team units are in the zone: increment turnsHeld.
 * If none: reset turnsHeld to 0.
 * Returns null extraction (cleared) if completed.
 */
export function tickExtraction(
  extraction: ExtractionProgress,
  players: PlayerState[],
  planets: PlanetState[]
): { extraction: ExtractionProgress | null; completed: boolean } {
  const unitsInZone = getTeamUnitsInZone(
    players,
    extraction.teamIndex,
    { planetIndex: extraction.planetIndex, surfaceDir: extraction.surfaceDir },
    planets
  );

  if (unitsInZone.length > 0) {
    const newHeld = extraction.turnsHeld + 1;
    if (newHeld >= extraction.turnsRequired) {
      return { extraction: null, completed: true };
    }
    return {
      extraction: { ...extraction, turnsHeld: newHeld },
      completed: false,
    };
  }

  // No units in zone — reset progress
  return {
    extraction: { ...extraction, turnsHeld: 0 },
    completed: false,
  };
}
